import { useEffect, useState } from "react";
import { useAppDispatch } from "../redux/hooks";
import { allCustomerNextPay, formatNumber } from "../redux/thunk";
import Pagination from "../components/Pagination";
import { IoSearchOutline } from "react-icons/io5";
import { FaPhone, FaRegCopy, FaWhatsapp } from "react-icons/fa6";
import { BsEye } from "react-icons/bs";
import toast from "react-hot-toast";
import LoadingSpinner from "../components/elements/LoadingSpinner";
import LoanDashboardModal from "../components/modals/LoanDashboardModal";

export const NextPay = () => {
  const [cycleFilter, setCycleFilter] = useState("");
  const [dateFilter, setDateFilter] = useState<string>("1");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [searchedUser, setSearchedUser] = useState<any>([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const searchForUsersWithEmail = (value: string) => {
    setSearchedUser(value);
    if (value.length > 0) {
      const searchResult =
        data?.filter((user: any) =>
          user.email?.toLowerCase().includes(value.toLowerCase())
        ) || null;
      setSearchResults(searchResult);
    } else {
      setSearchResults([]);
    }
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    setIsLoading(true);
    dispatch(allCustomerNextPay()).then((response: any) => {
      const resData = response.payload;
      // Check if resData is an array before calling reverse()
      if (Array.isArray(resData)) {
        setData(resData.reverse());
      } else {
        // Handle case when API returns an object (e.g., {message: "No wallets found"})
        setData([]);
      }
      setIsLoading(false);
    });
  }, [dispatch]);

  const filterAndMapData = () => {
    if (data.message) {
      return [];
    }

    const nextPayData = data.filter((data: any) => {
      if (cycleFilter === "") {
        return data.loanStatus;
      } else if (cycleFilter === "fCycle") {
        return data.loanStatus === "fCycle";
      } else if (cycleFilter === "sCycle") {
        return data.loanStatus === "sCycle";
      }
      return true;
    });
    return nextPayData.filter((data: any) => {
      const currentDate = new Date();
      const apiDate = new Date(data.nextPayDate);
      const timeDifference = apiDate.getTime() - currentDate.getTime();
      const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
      // console.log(`Number of days difference for ${data.nextPayDate}: ${daysDifference}`);

      if (dateFilter === "7+") {
        return daysDifference > 7;
      } else if (dateFilter !== "all") {
        const selectedDays = parseInt(dateFilter, 10);
        return daysDifference === selectedDays;
      } else {
        return true;
      }
    });
  };

  const filteredData = filterAndMapData();
  const [itemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastPost = currentPage * itemsPerPage;
  const indexOfFirstPost = indexOfLastPost - itemsPerPage;
  const mainData = searchResults.length > 0 ? searchResults : filteredData;
  const currentItems = mainData.slice(indexOfFirstPost, indexOfLastPost);

  // Pagination
  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const prevPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage !== Math.ceil(filteredData.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleCopyClick = async (text: any) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleViewDashboard = (customer: any) => {
    setSelectedCustomer(customer);
    setShowDashboardModal(true);
  };

  const handleCloseDashboard = () => {
    setShowDashboardModal(false);
    setSelectedCustomer(null);
  };

  return (
    <main className=" w-full">
      <div className="bg-white rounded-md shadow-md pb-6">
        <div
          className={`w-full overflow-x-auto ${
            isLoading && "animate-pulse h-[50vh]"
          }`}
        >
          <div
            className="flex items-center justify-between p-6"
            style={{ minWidth: "700px" }}
          >
            <div className="flex items-center justify-between w-full">
              <h1 className="text-lg font-semibold">Next Pay</h1>
              <div>
                <label htmlFor="filterOccupation">
                  Sort by:
                  <select
                    name="filterOccupation"
                    id="filterOccupation"
                    className="border p-2 ml-4 text-sm"
                    onChange={(e) => setCycleFilter(e.target.value)}
                  >
                    <option value="">Select cycle</option>
                    <option value="fCycle">First Cycle</option>
                    <option value="sCycle">Second Cycle</option>
                  </select>
                </label>
                <label htmlFor="filterDays">
                  <select
                    name="filterDays"
                    id="filterDays"
                    className="border p-2 ml-4 text-sm"
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <option value="1">Today</option>
                    <option value="2">1 day</option>
                    <option value="3">2 days</option>
                    <option value="4">3 days</option>
                    <option value="5">4 days</option>
                    <option value="6">5 days</option>
                    <option value="7">6 days</option>
                    <option value="7+">7+ days</option>
                    <option value="all">All</option>
                  </select>
                </label>
              </div>
            </div>
          </div>
          <div className="relative md:w-[30rem] w-fit mx-5 mb-5">
            <IoSearchOutline className="w-6 h-6 absolute top-[0.6rem] left-2 text-gray-300" />
            <input
              type="search"
              name="searchedUser"
              id="searchedUser"
              value={searchedUser}
              onChange={(e) => searchForUsersWithEmail(e.target.value)}
              placeholder="Search user using email"
              className="border p-2 rounded-md indent-7 w-full text-sm"
              disabled={data?.length === 0}
            />
          </div>

          <section className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: "1200px" }}>
              <thead className="bg-gray-50 font-bold text-left">
                <tr className="text-nowrap">
                  <th className="p-3 ">S/N</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Phone number</th>
                  <th className="p-3">Contact</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">BVN</th>
                  <th className="p-3">Loan status</th>
                  <th className="p-3">Loan amount</th>
                  <th className="p-3">Eligible amount</th>
                  <th className="p-3">Amount left</th>
                  <th className="p-3">Wallet balance</th>
                  <th className="p-3">Amount to be paid</th>
                  <th className="p-3">Next Pay Date</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody className="px-4">
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center p-10">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((data: any, index: any) => (
                    <tr className="border-b hover:bg-gray-50" key={index}>
                      <td className="text-secondary px-2 py-4 text-nowrap">
                        {index + indexOfFirstPost + 1}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {data?.fullname}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {data?.phoneNumber}
                      </td>
                      <td className="p-2 flex justify-evenly">
                        <button type="button" title="call customer">
                          <a href={`tel:${data?.phoneNumber}`}>
                            <FaPhone />
                          </a>
                        </button>
                        <button type="button" title="text via whatsapp">
                          <a
                            href={`https://wa.me/${data?.phoneNumber?.replace(
                              /^0/,
                              "234"
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaWhatsapp />
                          </a>
                        </button>
                      </td>
                      <td className="px-2 py-4 text-nowrap">{data?.email}</td>
                      <td className="px-2 py-4 text-nowrap flex items-end gap-2 justify-between">
                        <p>{data?.bvn}</p>
                        <button
                          type="button"
                          className=""
                          onClick={() => handleCopyClick(data?.bvn)}
                        >
                          <FaRegCopy className="w-5 h-5 text-gray-500" />
                        </button>
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {data?.loanStatus === "fCycle" ? (
                          <span>first</span>
                        ) : data?.loanStatus === "sCycle" ? (
                          <span>second</span>
                        ) : null}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {formatNumber((data?.loanAmount).toFixed(1))}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {formatNumber((data?.eligibleAmount).toFixed(1))}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {formatNumber((data?.amountLeft).toFixed(1))}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {formatNumber((data?.walletBalance).toFixed(1))}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {formatNumber((data?.amountToBePaid).toFixed(1))}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        {new Date(data?.nextPayDate).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td className="px-2 py-4 text-nowrap">
                        <button
                          type="button"
                          onClick={() => handleViewDashboard(data)}
                          className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-md transition-colors"
                          title="View Dashboard"
                        >
                          <BsEye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={14} className="text-center text-secondary p-3">
                      Data not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
          <section className="p-3 my-5">
            <Pagination
              length={
                searchResults.length > 0
                  ? searchResults.length
                  : filteredData.length
              }
              itemsPerPage={itemsPerPage}
              handlePagination={handlePagination}
              currentPage={currentPage}
              prevPage={prevPage}
              nextPage={nextPage}
            />
          </section>
        </div>
      </div>

      {/* Loan Dashboard Modal */}
      {selectedCustomer && (
        <LoanDashboardModal
          isOpen={showDashboardModal}
          onClose={handleCloseDashboard}
          customerData={{
            userId: selectedCustomer.userId,
            email: selectedCustomer.email,
            fullname: selectedCustomer.fullname,
            phoneNumber: selectedCustomer.phoneNumber,
            bvn: selectedCustomer.bvn,
            employment: selectedCustomer.employment || 'N/A',
            loanAmount: selectedCustomer.loanAmount,
            eligibleAmount: selectedCustomer.eligibleAmount,
            amountLeft: selectedCustomer.amountLeft,
            amountToBePaid: selectedCustomer.amountToBePaid,
            nextPayDate: selectedCustomer.nextPayDate,
            cycle: selectedCustomer.cycle,
            dailyInterest: selectedCustomer.dailyInterest,
            dailyInterestIncrement: selectedCustomer.dailyInterestIncrement,
            loanStatus: selectedCustomer.loanStatus,
            walletBalance: selectedCustomer.walletBalance,
          }}
        />
      )}
    </main>
  );
};
